import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { AuditOperationBadge } from "./-components/audit-operation-badge";
import { SortDirection } from "@/interfaces/query-options-dto";
import { buildDataTableQueryOptions, ensureRouteQueriesData } from "@/lib/route-loader";
import { dataTableFiltersSearchSchema } from "@/lib/route-search";
import type { AuditDTO } from "@/orval/model";
import {
  getAuditGetAuditLogFilterConfigQueryOptions,
  getAuditGetAuditLogFilterConfigQueryKey,
  auditGetAuditLogFilterConfig,
  auditGetAuditLogsList,
} from "@/orval/audit";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { AuditDetailDialog } from "./-components/audit-detail-dialog";
import { format, parseISO } from "date-fns";
import { PortalPageHeader } from "@/components/portal-page-header";
import { EntityAuditHistoryDialog } from "./-components/entity-audit-history-dialog";
import { getAuditPrimaryKeyDisplayValue } from "./-components/audit-formatting";

const columns: ColumnDef<AuditDTO>[] = [
  {
    accessorKey: "dateTime",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date & Time" />,
    size: 150,
    enableSorting: true,
    cell: ({ getValue }) => {
      const date = getValue() as string;
      return format(parseISO(date + "Z"), "MM/dd/yyyy h:mm:ss a");
    },
  },
  {
    accessorKey: "type",
    header: "Operation",
    size: 100,
    enableSorting: false,
    cell: ({ row }) => <AuditOperationBadge type={row.original.type} />,
  },
  {
    accessorKey: "tableName",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Table" />,
    size: 120,
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.user?.email || "System",
    id: "user",
    header: "User",
    size: 200,
    enableSorting: false,
    cell: ({ getValue }) => {
      const email = getValue() as string;
      return email || <span className="text-muted-foreground">System</span>;
    },
  },
  {
    accessorKey: "primaryKey",
    header: "Primary Key",
    size: 100,
    enableSorting: false,
    cell: ({ getValue }) => {
      const pk = getValue() as string;
      return <code className="text-xs bg-muted px-1 py-0.5 rounded">{getAuditPrimaryKeyDisplayValue(pk)}</code>;
    },
  },
];

const SiteAdminAuditLogsPage = () => {
  const [selectedAuditId, setSelectedAuditId] = useState<number | null>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [entityHistory, setEntityHistory] = useState<{ tableName: string; primaryKey: string } | null>(null);
  const [isEntityHistoryOpen, setEntityHistoryOpen] = useState(false);

  const handleRowClick = (audit: AuditDTO) => {
    setSelectedAuditId(audit.id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAuditId(null);
  };

  const handleViewEntityHistory = (entity: { tableName: string; primaryKey: string }) => {
    setEntityHistory(entity);
    setEntityHistoryOpen(true);
    setDialogOpen(false);
    setSelectedAuditId(null);
  };

  const handleCloseEntityHistory = () => {
    setEntityHistoryOpen(false);
    setEntityHistory(null);
  };

  const handleEntityHistoryAuditSelected = (auditId: number) => {
    setSelectedAuditId(auditId);
    setDialogOpen(true);
  };

  const getAuditLogs = auditGetAuditLogsList;
  const getAuditFiltersConfig = auditGetAuditLogFilterConfig;

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader className="mb-8" title="Audit Logs" subtitle="View system audit trail and data changes" />

      <DataTable
        columns={columns}
        queryFn={(queryOptions, signal, filters) => getAuditLogs(filters ?? [], queryOptions, signal)}
        queryKeyFn={(params, filters) => ["audit-logs", params, filters]}
        filtersQueryFn={(signal) => getAuditFiltersConfig(signal)}
        filtersQueryKeyFn={() => getAuditGetAuditLogFilterConfigQueryKey()}
        onRowClick={handleRowClick}
        defaultSorting={[{ id: "dateTime", desc: true }]}
        getRowIdFn={(row) => row.id.toString()}
      />

      <EntityAuditHistoryDialog
        isOpen={isEntityHistoryOpen}
        onClose={handleCloseEntityHistory}
        tableName={entityHistory?.tableName ?? null}
        primaryKey={entityHistory?.primaryKey ?? null}
        onAuditSelected={handleEntityHistoryAuditSelected}
      />

      <AuditDetailDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        auditId={selectedAuditId}
        onViewEntityHistory={handleViewEntityHistory}
      />
    </div>
  );
};

export const Route = createFileRoute("/_app/site-admin/audit-logs")({
  validateSearch: dataTableFiltersSearchSchema,
  loader: ({ context, location }) => {
    const { searchString, filters } = dataTableFiltersSearchSchema.parse(location.search);
    const queryOptions = buildDataTableQueryOptions({
      searchString,
      sortField: "dateTime",
      sortDirection: SortDirection.Descending,
    });

    return ensureRouteQueriesData({ context }, [
      getAuditGetAuditLogFilterConfigQueryOptions(),
      {
        queryKey: ["audit-logs", queryOptions, filters],
        queryFn: () => auditGetAuditLogsList(filters ?? [], queryOptions),
      },
    ]);
  },
  component: SiteAdminAuditLogsPage,
});
